import { memo, useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { Handle, Position } from '@xyflow/react';
import { AnimatePresence, motion } from 'motion/react';
import { CallRouteV1 } from '@frontend/api-call-route';
import { useTranslation } from '@frontend/i18n';
import { usePreference } from '@frontend/user-preferences';
import { theme } from '@frontend/theme';
import { Button, Text, useTooltip } from '@frontend/design-system';
import { trackingId } from '../../../../../tracking';
import { IncomingDialable, getIncomingDialablesGridData } from '../../../helpers/incoming-dialables';
import { SharedNodeProps } from '../data';

export const INCOMING_DIALABLES_SOURCE_HANDLE_ID = 'incoming-dialables-source';
export const INCOMING_DIALABLES_TARGET_HANDLE_ID = 'incoming-dialables-target';

export const IncomingDialablesNode = memo(
  ({
    incomingDialables = [],
    onIncomingDialablesMoreClick,
  }: SharedNodeProps<CallRouteV1.Types.NodeType | 'incomingDialables'>) => {
    const { t } = useTranslation('phone');
    const [incomingDialablesFlowPreference, setIncomingDialablesFlowPreference] =
      usePreference('CallRouteVisibleNumbers');
    const isExpanded = incomingDialablesFlowPreference;
    const hasRenderedRef = useRef(false);
    const { visibleDialables, hiddenCount } = getIncomingDialablesGridData(incomingDialables);

    /**
     * This effect is used to control whether the animation should run on the initial render.
     * We don't want the animation to run on the initial render because it looks janky when the node first appears with the dialables.
     * By setting hasRenderedRef.current to true after the first render, we can conditionally disable the animation on the initial render.
     */
    useEffect(() => {
      hasRenderedRef.current = true;
    }, []);

    if (!incomingDialables.length) {
      return null;
    }

    return (
      <div
        css={css`
          position: relative;
          width: 24px;
          cursor: default;
        `}
      >
        <Handle
          type='target'
          position={Position.Left}
          id={INCOMING_DIALABLES_TARGET_HANDLE_ID}
          isConnectable={true}
          style={{ visibility: 'hidden' }}
        />

        <Button
          variant='secondary'
          size='small'
          iconName={isExpanded ? 'collapse-view' : 'collapse-right'}
          css={{
            boxShadow: isExpanded ? theme.shadows.floating : theme.shadows.light,
            height: 24,
            padding: theme.spacing(0, 0.5),
            width: 'max-content',
            border: `1px solid ${theme.colorsV2.black(10)}`,
            backgroundColor: theme.colors.white,
            cursor: 'pointer',
            '&:hover': {
              background: theme.colors.white,
              borderColor: theme.colorsV2.black(35),
              boxShadow: theme.shadows.floating,
            },
          }}
          onClick={() => {
            setIncomingDialablesFlowPreference(!isExpanded);
          }}
          trackingId={trackingId({
            feature: 'call-route',
            context: 'setting',
            details: isExpanded ? 'collapse-incoming-dialables' : 'expand-incoming-dialables',
          })}
        >
          {isExpanded ? null : <DialablesCountPill count={incomingDialables.length} />}
        </Button>

        <AnimatePresence initial={false}>
          {isExpanded && (
            <div
              css={css`
                position: absolute;
                left: calc(100% + ${theme.spacing(1)});
                top: 50%;
                transform: translateY(-50%);
                width: max-content;
                z-index: 1;
              `}
            >
              <motion.div
                initial={hasRenderedRef.current ? { opacity: 0, x: -10 } : false}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 0 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
                css={css`
                  position: relative;
                  display: grid;
                  grid-template-columns: repeat(3, max-content);
                  gap: ${theme.spacing(0.5)};
                `}
              >
                {visibleDialables.map((dialable, index) => (
                  <DialableLabel key={`${dialable.label}-${index}`} dialable={dialable} />
                ))}
                {hiddenCount > 0 && (
                  <Button
                    variant='tertiary'
                    size='small'
                    onClick={onIncomingDialablesMoreClick}
                    css={css`
                      position: absolute;
                      top: calc(100% + ${theme.spacing(0.5)});
                      left: 50%;
                      transform: translateX(-50%);
                    `}
                    trackingId={trackingId({
                      feature: 'call-route',
                      context: 'setting',
                      details: 'incoming-dialables-more-button',
                    })}
                  >
                    {t('+{{count}} more', { count: hiddenCount })}
                  </Button>
                )}
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    );
  }
);

const DialableLabel = ({ dialable }: { dialable: IncomingDialable }) => {
  const { Tooltip, tooltipProps, triggerProps } = useTooltip({ placement: 'top' });

  return (
    <>
      <div
        {...triggerProps}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 24,
          width: 112,
          padding: theme.spacing(0, 1),
          borderRadius: theme.borderRadius.small,
          border: `0.5px solid ${theme.colorsV2.black(20)}`,
          backgroundColor: theme.colors.white,
          cursor: 'default',
          boxShadow: theme.shadows.floating,
        }}
      >
        <Text size='small'>{dialable.label}</Text>
      </div>
      <Tooltip {...tooltipProps}>{dialable.tooltip || dialable.label}</Tooltip>
    </>
  );
};

IncomingDialablesNode.displayName = 'IncomingDialablesNode';

export const DialablesCountPill = ({ count }: { count: number }) => {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 16,
          minWidth: 16,
          borderRadius: theme.borderRadius.large,
          backgroundColor: theme.colorsV2.blue50(25),
          color: theme.colors.white,
          padding: theme.spacing(0, 0.5),
        }}
      >
        <Text size='small'>{count}</Text>
      </span>
      <Text size='small' weight='regular'>
        #
      </Text>
    </span>
  );
};
